/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { ActionTypes } from 'models/action.model';
import { collectionsStore, domainStore, simpleListsStore } from 'stores';

import S3TenantAssetImage from 'components/Assets/S3TenantAssetImage';
import AdditionalFields from 'components/Collections/CollectionRow/AdditionalFields';
import AppearanceFields from 'components/Collections/CollectionRow/AppearanceFields';
import BasicFields from 'components/Collections/CollectionRow/BasicFields';
import Preview from 'components/Collections/CollectionRow/Preivew';

interface Props {
  closeDrawer?: any;
  collection: any;
  context?: string;
  isDroppedOnLoad?: boolean;
}

interface InternalProps extends Props {
  activeMenu: string;
  isDropped: boolean;
  isMenuOpen: boolean;
  isPreviewOpen?: boolean;
  setActiveMenu: Dispatch<SetStateAction<string>>;
  setIsDropped: Dispatch<SetStateAction<boolean>>;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  setIsPreviewOpen?: Dispatch<SetStateAction<boolean>>;
}

enum MenuOptions {
  BASIC = 'basic info',
  APPEARANCE = 'appearance',
  ADDITIONAL = 'additional info',
  HISTORY = 'history',
  RANDOM = 'random',
}

function HamburgerMenu(props: {
  children: JSX.Element | Array<JSX.Element>;
  collectionId: number;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
}): JSX.Element {
  return (
    <>
      <div
        className="backsplash"
        onClick={(): void => {
          props.setIsMenuOpen(false);
        }}
      ></div>
      <div className={`hamburger-menu-card`}>{props.children}</div>
    </>
  );
}

function DeleteOption(props: { collectionId: number }): JSX.Element {
  return (
    <>
      <div
        className="menu-option"
        onClick={(): void => {
          window.siteWideComponent.show({
            approve: () => {
              collectionsStore
                .deleteItem({
                  domainStore,
                  id: props.collectionId,
                })
                .then(() => {
                  collectionsStore.handleTableAction(
                    { domainStore },
                    {
                      payload: { page: collectionsStore.page.toString() },
                      type: ActionTypes.PAGINATE,
                    },
                  );
                });
            },
            body: `Are you sure you want to delete collection ${
              simpleListsStore.collections.get(props.collectionId).name
            }?`,
            title: 'Delete Confirmation',
          });
        }}
      >
        Delete
      </div>
    </>
  );
}

function MoreInfoOption(props: { collectionId: number }): JSX.Element {
  const [isClicked, setIsClicked] = useState(false);

  return (
    <>
      {isClicked && (
        <Redirect
          from="*"
          to={`/content/collections/detail/${props.collectionId}`}
        />
      )}
      <div
        className="menu-option"
        onClick={(): void => {
          setIsClicked(true);
        }}
      >
        More Info
      </div>
    </>
  );
}

function RowHeader(props: InternalProps): JSX.Element {
  const {
    collection,
    context,
    isDropped,
    isMenuOpen,
    isPreviewOpen,
    setIsDropped,
    setIsMenuOpen,
    setIsPreviewOpen,
  } = props;

  return (
    <>
      <div
        className={`detail-row-wrapper ${props.isDropped && 'active'}`}
        onClick={(): void => {
          if (context !== 'detail') {
            if (props.closeDrawer) {
              props.closeDrawer();
            }
            setIsDropped(!isDropped);
          }
        }}
      >
        <div className="detail-row-body">
          <div className="detail-row-thumbnail">
            {collection.thumbnail.s3Key !== undefined && (
              <S3TenantAssetImage s3Key={collection.thumbnail.s3Key} />
            )}
            {collection.thumbnail.s3Key === undefined && (
              <i className="fas fa-sitemap" />
            )}
          </div>
          <div className="detail-row-title">
            <span>{collection.name}</span>
          </div>
          {isDropped && context !== 'detail' && (
            <div className="detail-done">Done</div>
          )}
          {!isDropped && (
            <>
              <div
                className="detail-row-meta preview-button"
                onClick={(e): void => {
                  e.stopPropagation();
                  setIsPreviewOpen(!isPreviewOpen);
                }}
              >
                <i className="fas fa-folder-tree" />
                {isPreviewOpen && (
                  <Preview
                    collectionId={collection.id}
                    togglePreview={setIsPreviewOpen}
                  />
                )}
              </div>
              <div className="detail-row-meta">
                <div className="meta-text">
                  {dayjs(collection.updated).format('MM / DD / YYYY')}
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="hamburger-menu-card-contain"
              onClick={(e): void => {
                e.stopPropagation();
              }}
            >
              <i
                className="fal fa-ellipsis-v"
                onClick={(e): void => {
                  e.stopPropagation();
                  setIsMenuOpen(!isMenuOpen);
                }}
              ></i>
              {isMenuOpen && (
                <HamburgerMenu
                  collectionId={props.collection.id}
                  setIsMenuOpen={setIsMenuOpen}
                >
                  <MoreInfoOption collectionId={props.collection.id} />
                  <DeleteOption collectionId={props.collection.id} />
                </HamburgerMenu>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Menu(props: InternalProps): JSX.Element {
  const { activeMenu, setActiveMenu } = props;

  return (
    <>
      <div className="menu">
        <div
          className={`${activeMenu === MenuOptions.BASIC && 'active'}`}
          onClick={(): void => {
            setActiveMenu(MenuOptions.BASIC);
          }}
        >
          <i className="fas fa-sitemap mr-2" /> Basic Info
        </div>
        <div
          className={`${activeMenu === MenuOptions.APPEARANCE && 'active'}`}
          onClick={(): void => {
            setActiveMenu(MenuOptions.APPEARANCE);
          }}
        >
          <i className="fas fa-images mr-2" /> Appearance
        </div>
        <div
          className={`${activeMenu === MenuOptions.ADDITIONAL && 'active'}`}
          onClick={(): void => {
            setActiveMenu(MenuOptions.ADDITIONAL);
          }}
        >
          <i className="fas fa-info-circle mr-2" /> Additional Info
        </div>
      </div>
    </>
  );
}

function Content(props: InternalProps): JSX.Element {
  const { activeMenu } = props;

  switch (activeMenu) {
    case MenuOptions.BASIC:
      return <BasicFields {...props} />;
      break;
    case MenuOptions.APPEARANCE:
      return <AppearanceFields {...props} />;
      break;
    case MenuOptions.ADDITIONAL:
      return <AdditionalFields {...props} />;
      break;
    case MenuOptions.HISTORY:
      return <>history</>;
      break;
    case MenuOptions.RANDOM:
      return <>random</>;
      break;
    default:
      return <>content</>;
  }
}

const DroppedContent: (props: InternalProps) => JSX.Element = observer(
  (props: InternalProps) => {
    const { isDropped } = props;

    return (
      <>
        <div
          className={`detail-row-dropped-content ${isDropped && ' dropped'}`}
          tabIndex={0}
        >
          <Menu {...props} />
          <Content {...props} />
        </div>
      </>
    );
  },
);

function CollectionRow(props: Props): JSX.Element {
  const [isDropped, setIsDropped] = useState(
    props.isDroppedOnLoad ? true : false,
  );
  const [hasDroppedClass, setHasDroppedClass] = useState(true);
  const [activeMenu, setActiveMenu] = useState(MenuOptions.BASIC);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (isDropped) {
      setHasDroppedClass(true);
    } else {
      setTimeout((): void => {
        setHasDroppedClass(false);
      }, 400);
    }
  }, [isDropped]);

  return (
    <>
      {isDropped && (
        <div
          className="detail-backsplash"
          onClick={(): void => {
            if (props.context !== 'detail') {
              setIsDropped(false);
            }
          }}
        ></div>
      )}
      <div
        className={`detail-view-wrapper ${hasDroppedClass && 'dropped'} ${
          isMenuOpen && 'hamburger-open'
        } ${isPreviewOpen && 'hamburger-open'}`}
        tabIndex={0}
        onKeyUp={(e): void => {
          if (e.key === 'Enter') {
            if ((e.target as any).className.includes('dropped')) {
              setIsDropped(false);
            }
          }
          if (e.key === 'Escape') {
            setIsDropped(false);
          }
        }}
      >
        <RowHeader
          {...props}
          activeMenu={activeMenu}
          isDropped={isDropped}
          isMenuOpen={isMenuOpen}
          isPreviewOpen={isPreviewOpen}
          setActiveMenu={setActiveMenu}
          setIsDropped={setIsDropped}
          setIsMenuOpen={setIsMenuOpen}
          setIsPreviewOpen={setIsPreviewOpen}
        />
        <DroppedContent
          {...props}
          activeMenu={activeMenu}
          isDropped={isDropped}
          isMenuOpen={isMenuOpen}
          setActiveMenu={setActiveMenu}
          setIsDropped={setIsDropped}
          setIsMenuOpen={setIsMenuOpen}
        />
      </div>
    </>
  );
}

export default CollectionRow;
